import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FiEdit2, FiTrash2, FiX, FiCreditCard, FiDollarSign, FiInfo, FiPercent, FiSearch, FiPlus } from 'react-icons/fi';

// Animated loader component
const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Processing...</p>
    </div>
  </div>
);

// Confirmation dialog component
const ConfirmDialog = ({ isOpen, title, message, onConfirm, onCancel }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      <div className="bg-white w-96 rounded-xl shadow-2xl transform transition-transform duration-300 ease-out animate-zoom-in overflow-hidden">
        <div className="bg-gradient-to-r from-red-600 to-pink-600 px-6 py-4">
          <h3 className="text-lg font-semibold text-white">{title}</h3>
        </div>
        <div className="p-6">
          <p className="text-gray-700">{message}</p>
          <div className="flex justify-end gap-4 mt-6">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-4 focus:ring-gray-200 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-red-600 to-pink-600 rounded-lg hover:from-red-700 hover:to-pink-700 focus:ring-4 focus:ring-red-300 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


const CardList = ({ setCardlistModal, fetchData, fetchData1, cardslist }) => {
  const [loading, setLoading] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, id: null });
  const [editModal, setEditModal] = useState({ isOpen: false, card: null });
  const [filteredCards, setFilteredCards] = useState(cardslist);
  
  const searchRef = useRef(null);

  // Focus the search input when component mounts
  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
  }, []);

  // Update filtered cards when search term or cardslist changes
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCards(cardslist);
      return;
    }
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    const filtered = cardslist.filter(card => 
      card.CardName.toLowerCase().includes(lowerSearchTerm) || 
      (card.Description && card.Description.toLowerCase().includes(lowerSearchTerm))
    );
    
    setFilteredCards(filtered);
  }, [searchTerm, cardslist]);

  const handleDelete = async (cardId) => {
    setConfirmDialog({ isOpen: false, id: null });
    setLoading(true);
    
    try {
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 800));
      
      console.log('Card deleted:', cardId);
      
      // Refresh data
      if (typeof fetchData === 'function') {
        fetchData();
      }
      
      if (typeof fetchData1 === 'function') {
        fetchData1();
      }
    } catch (error) {
      console.error('Error deleting card:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditModal = (card) => {
    setEditModal({ isOpen: true, card });
  };
  
  const handleOpenAddModal = () => {
    setEditModal({ isOpen: true, card: null });
  };
  
  const handleSaveCard = async (cardData) => {
    setLoading(true);
    
    try {
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 800));
      
      console.log('Card saved:', cardData);
      
      // Close modal
      setEditModal({ isOpen: false, card: null });
      
      // Refresh data
      if (typeof fetchData === 'function') {
        fetchData();
      }
      
      if (typeof fetchData1 === 'function') {
        fetchData1();
      }
    } catch (error) {
      console.error('Error saving card:', error);
    } finally {
      setLoading(false);
    }
  };

  // Determine if table needs empty rows to maintain a consistent height
  const rowsToFill = 10;
  const additionalRows = Math.max(0, rowsToFill - filteredCards.length);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      {loading && <Spinner />}
      
      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title="Confirm Deletion"
        message="Are you sure you want to delete this card? This action cannot be undone."
        onConfirm={() => handleDelete(confirmDialog.id)}
        onCancel={() => setConfirmDialog({ isOpen: false, id: null })}
      />
      
      <div className="bg-white w-4/5 max-w-5xl max-h-[70vh] rounded-2xl shadow-2xl transform transition-transform duration-300 ease-out animate-zoom-in overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-5 flex justify-between items-center sticky top-0 z-20">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <FiCreditCard className="mr-2" /> Cards Management
          </h2>
          <button 
            onClick={() => setCardlistModal(false)}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors duration-200"
            aria-label="Close modal"
          >
            <FiX size={20} />
          </button>
        </div>
        
        {/* Toolbar */}
        <div className="flex justify-between items-center p-6 bg-gray-50 border-b border-gray-100">
          <div className="relative w-64">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-black">
              <FiSearch />
            </div>
            <input
              ref={searchRef}
              type="text"
              placeholder="Search cards..."
              className="w-full pl-10 pr-3 py-2 border border-balck focus:border-indigo-500 focus:ring-indigo-200 bg-white rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <button
            onClick={handleOpenAddModal}
            className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg hover:from-indigo-700 hover:to-purple-700 focus:ring-4 focus:ring-indigo-300 transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
          >
            <FiPlus className="mr-2" /> Add New Card
          </button>
        </div>
        
        {/* Table Container */}
        <div className="overflow-auto max-h-[calc(70vh-220px)] pb-4">
          {filteredCards.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <FiInfo className="mx-auto mb-3" size={32} />
              <p className="text-lg font-medium">No cards found</p>
              <p className="text-sm mt-1">
                {searchTerm ? 'Try a different search term' : 'Click "Add New Card" to create one'}
              </p>
            </div>
          ) : (
            <table className="w-full border-collapse table-fixed">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="w-1/4 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                    <div className="flex items-center gap-1">
                      <span className="text-black">Card Name</span>
                    </div>
                  </th>
                  <th className="w-1/4 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                    <div className="flex items-center gap-1">
                      <span className="text-black">Description</span>
                    </div>
                  </th>
                  <th className="w-1/6 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                    <div className="flex items-center gap-1">
                      <span className="text-black">Amount</span>
                    </div>
                  </th>
                  <th className="w-1/6 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                    <div className="flex items-center gap-1">
                      <span className="text-black">Discount</span>
                    </div>
                  </th>
                  <th className="w-1/6 px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                    <span className="text-black">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredCards.map((card, index) => (
                  <tr 
                    key={card.CardID || index}
                    onMouseEnter={() => setHoveredRow(card.CardID)}
                    onMouseLeave={() => setHoveredRow(null)}
                    className={`transition-colors duration-200 ${hoveredRow === card.CardID ? 'bg-indigo-50' : (index % 2 === 0 ? 'bg-white' : 'bg-gray-50')}`}
                  >
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 border-b border-gray-100 truncate">{card.CardName}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 border-b border-gray-100 truncate">{card.Description || '-'}</td>
                    <td className="px-6 py-4 text-sm font-medium border-b border-gray-100">
                      <div className="flex items-center">
                        <span className="text-indigo-600">₹</span>
                        <span className="ml-1">{card.CardAmount}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm border-b border-gray-100">
                      {card.DiscPercentage !== null && card.DiscPercentage > 0 ? (
                        <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-1 rounded-full">
                          {card.DiscPercentage}%
                        </span>
                      ) : (
                        <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2.5 py-1 rounded-full">
                          No discount
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 border-b border-gray-100">
                      <div className="flex space-x-2 items-center justify-center">
                        <button
                          onClick={() => handleOpenEditModal(card)}
                          className="text-indigo-600 hover:text-indigo-800 p-1.5 rounded-full hover:bg-indigo-100 transition-colors"
                          title="Edit Card"
                        >
                          <FiEdit2 size={18} />
                        </button>
                        <button
                          onClick={() => setConfirmDialog({ isOpen: true, id: card.CardID })}
                          className="text-red-600 hover:text-red-800 p-1.5 rounded-full hover:bg-red-50 transition-colors"
                          title="Delete Card"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {Array.from({ length: additionalRows }).map((_, index) => (
                  <tr key={`empty-${index}`} className={index % 2 !== 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="px-6 py-4 text-sm border-b border-gray-100">&nbsp;</td>
                    <td className="px-6 py-4 text-sm border-b border-gray-100">&nbsp;</td>
                    <td className="px-6 py-4 text-sm border-b border-gray-100">&nbsp;</td>
                    <td className="px-6 py-4 text-sm border-b border-gray-100">&nbsp;</td>
                    <td className="px-6 py-4 text-sm border-b border-gray-100">&nbsp;</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 pt-4 pb-6 border-t border-gray-100 bg-white sticky bottom-0">
          <div className="text-sm text-black">
            {filteredCards.length} {filteredCards.length === 1 ? 'card' : 'cards'} found
            {searchTerm && ` for "${searchTerm}"`}
          </div>
          <button
            onClick={() => setCardlistModal(false)}
            className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg hover:from-indigo-700 hover:to-purple-700 focus:ring-4 focus:ring-indigo-300 transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
          >
            <FiX className="mr-2" /> Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CardList;

